import { AppDock, DOCK_SPACER_HEIGHT_REM } from "./dock"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { useCallback, useEffect, useRef } from "react"

import { AppSidebar } from "./Sidebar"
import { Button } from "@/components/ui/button"
import { Header } from "./Header"
import { MobileHeader } from "./MobileHeader"
import { PageErrorBoundary } from "./PageErrorBoundary"
import { getNotificationPermission } from "@/lib/firebase"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { useAuth } from "@/contexts/auth-context"
import { useBreakpoint } from "@/hooks/use-breakpoint"
import { useIntl } from "react-intl"
import type { Language } from "@/components/I18nProvider"
import { useLanguage } from "@/components/I18nProvider"
import { useTheme } from "@/hooks/use-theme"
import { useUserPreferences } from "@/hooks/use-user-preferences"

export interface LayoutProps {
  children: React.ReactNode
  title: string
  /** Reset page error boundary when route changes. */
  pageResetKey?: string
  /** Optional warning message in header (e.g. policy approaching limit) */
  headerWarning?: string | null
  /** Ref for the main scroll container (e.g. for scroll-to-top on route change) */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}

export function Layout({
  children,
  title,
  pageResetKey,
  headerWarning,
  scrollContainerRef,
}: LayoutProps) {
  const intl = useIntl()
  const { language, setLanguage } = useLanguage()
  const { theme, setTheme } = useTheme()
  const { isVerified } = useAuth()
  const { preferences, updatePreferences } = useUserPreferences(!!isVerified)
  const { isSmallDesktop } = useBreakpoint()
  const hasShownPushPermissionToastRef = useRef(false)

  useEffect(() => {
    if (!preferences) return
    const apiTheme = preferences.theme
    if (theme !== apiTheme) setTheme(apiTheme)
  }, [preferences, theme, setTheme])

  useEffect(() => {
    if (!isVerified) {
      hasShownPushPermissionToastRef.current = false
      return
    }
    if (!preferences?.push_notification?.enabled) {
      hasShownPushPermissionToastRef.current = false
      return
    }
    if (getNotificationPermission() === "granted") {
      hasShownPushPermissionToastRef.current = false
      return
    }
    if (hasShownPushPermissionToastRef.current) return

    hasShownPushPermissionToastRef.current = true
    toast.warning(
      intl.formatMessage({
        id: "settings.notifications.pushPermissionWarning",
      })
    )
  }, [isVerified, preferences?.push_notification?.enabled, intl])

  const onLanguageChange = useCallback(
    (lang: Language) => {
      setLanguage(lang)
      void updatePreferences({ language: lang }).catch(() => {
        // Ignore persistence failure from header quick switch to avoid noisy generic toasts.
      })
    },
    [setLanguage, updatePreferences]
  )

  return (
    <SidebarProvider>
      <AppSidebar
        logoAlt={intl.formatMessage({ id: "layout.sidebarLogoAlt" })}
      />

      <SidebarInset>
        <div
          className="bg-background-gradient pointer-events-none absolute top-0 right-0 left-0 z-0 h-[500px] w-full"
          aria-hidden
        />

        <Header
          title={title}
          language={language}
          onLanguageChange={onLanguageChange}
          warningMessage={headerWarning}
          className="hidden lg:flex"
        />

        <MobileHeader
          title={title}
          logoAlt={intl.formatMessage({ id: "layout.sidebarLogoAlt" })}
          className="flex lg:hidden"
        />

        <div
          ref={scrollContainerRef}
          className={cn(
            "scrollbar-primary relative z-10 min-w-0 flex-1 overflow-x-hidden overflow-y-auto"
          )}
          style={
            !isSmallDesktop
              ? {
                  paddingBottom: `calc(${DOCK_SPACER_HEIGHT_REM}rem + env(safe-area-inset-bottom, 0px))`,
                }
              : undefined
          }
        >
          <main className="min-w-0 p-4 lg:p-6 xl:pr-3">
            <PageErrorBoundary
              resetKey={pageResetKey}
              fallback={
                <div className="border-border bg-background flex min-h-60 flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center">
                  <p className="text-primary-text app-text-title-medium">
                    {intl.formatMessage({ id: "layout.pageErrorTitle" })}
                  </p>
                  <p className="text-secondary-text app-text-body-small">
                    {intl.formatMessage({
                      id: "layout.pageErrorDescription",
                    })}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                  >
                    {intl.formatMessage({ id: "layout.pageErrorReload" })}
                  </Button>
                </div>
              }
            >
              {children}
            </PageErrorBoundary>
          </main>
        </div>

        {!isSmallDesktop && <AppDock />}
      </SidebarInset>
    </SidebarProvider>
  )
}
